/**
 * `TrustEngineView` — ADR-0231 ops2026 "Trust engine (shadow)" panel.
 *
 * Read-only. Shows what the Agent Trust Engine slice 1 is producing in
 * shadow mode: report volume by evidence tier, the shadow verdict mix,
 * and the latest "hide the proof" backtest runs (attested, and the
 * clearly-labelled counterfactual run over historical anonymous
 * traffic). Nothing on this page is shown to merchants.
 *
 * A 404 from the API (SPA deployed before the API) renders a "not
 * deployed yet" card, like the Agent registry page.
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import type { TrustBacktestRunView, TrustEngineAdminResponse } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import { formatCount, formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { LiveVerdictsPanel } from './LiveVerdictsPanel.js';
import {
  COUNTERFACTUAL_NOTE,
  LABELS,
  LABEL_LABEL,
  TIERS,
  TIER_LABEL,
  formatRate,
  share,
} from './trust-helpers.js';
import { HardeningPanel, ReasonContributionTable } from './HardeningPanel.js';
import { ReadinessPanel } from './ReadinessPanel.js';
import { BaselinesPanel } from './BaselinesPanel.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export function TrustEngineView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [data, setData] = useState<TrustEngineAdminResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  // ADR-0236 — flag changes in Readiness / the flags panel refresh each other.
  const [flagsToken, setFlagsToken] = useState(0);
  const [includeAcked, setIncludeAcked] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.getTrustEngine(includeAcked ? { include_acked: true } : {}));
      setMissing(false);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load the trust engine panel.');
    } finally {
      setLoading(false);
    }
  }, [api, includeAcked]);

  useEffect(() => {
    void load();
  }, [load]);

  const ackAlert = useCallback(async (alertId: number, note: string): Promise<void> => {
    await api.ackTrustKeyAlert(alertId, note);
    await load();
  }, [api, load]);

  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const, marginBottom: 8 };
  const th = { textAlign: 'left' as const, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' as const, color: C.muted, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' as const };
  const td = { padding: '6px 8px', color: C.body, borderBottom: `1px solid ${C.border}` };
  const num = { ...td, fontFamily: monoFont, fontSize: 11.5, textAlign: 'right' as const };
  const section = (title: string): JSX.Element => <div style={{ ...kicker, marginTop: 24 }}>{title}</div>;

  const stat = (label: string, value: ReactNode, sub?: string): JSX.Element => (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted }}>{label}</div>
      <div style={{ fontFamily: serifFont, fontSize: 24, color: C.text }}>{value}</div>
      {sub !== undefined ? <div style={{ fontFamily: sansFont, fontSize: 11.5, color: C.muted }}>{sub}</div> : null}
    </div>
  );

  const renderRun = (title: string, run: TrustBacktestRunView | null, counterfactual: boolean): JSX.Element => (
    <Card style={{ padding: 16, marginBottom: 16 }} data-testid={counterfactual ? 'run-counterfactual' : 'run-attested'}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: sansFont, fontSize: 14, fontWeight: 600, color: C.text }}>{title}</div>
        {run !== null ? (
          <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
            {formatIsoCompact(run.window_start)} → {formatIsoCompact(run.window_end)} · {run.rule_version} · run {formatIsoCompact(run.created_at)}
          </div>
        ) : null}
      </div>
      {counterfactual ? (
        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.amberFg, marginTop: 6 }}>{COUNTERFACTUAL_NOTE}</div>
      ) : null}
      {run === null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 8 }}>No run yet.</div>
      ) : (
        <>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginTop: 6 }}>
            {formatCount(run.verified_total)} verified observations evaluated with the proof hidden ·{' '}
            {formatCount(run.skipped_unverified)} skipped (not verified).
          </div>
          <div style={{ ...kicker, marginTop: 14 }}>False accusations by agent · target ≤ 0.1% at the 95% upper bound</div>
          {run.by_agent.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No verified traffic in the window.</div>
          ) : (
            <TableScroll minWidth={760}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                <thead>
                  <tr>
                    {['Agent', 'n', 'Agent-days', 'False acc.', 'Rate', '95% upper', 'Suspicious', 'Target'].map((h) => (
                      <th key={h} style={h === 'Agent' ? th : { ...th, textAlign: 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {run.by_agent.map((a) => (
                    <tr key={a.agent_id}>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{a.agent_id}</td>
                      <td style={num}>{formatCount(a.n)}</td>
                      <td style={num}>{formatCount(a.agent_days)}</td>
                      <td style={num}>{formatCount(a.false_accusations)}</td>
                      <td style={num}>{formatRate(a.false_accusation_rate)}</td>
                      <td style={num}>{formatRate(a.false_accusation_upper95)}</td>
                      <td style={num}>{formatCount(a.suspicious_flags)} ({formatRate(a.suspicious_rate)})</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <Pill tone={a.meets_target ? 'lime' : 'neutral'}>{a.meets_target ? 'meets' : 'not yet'}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
          <div style={{ ...kicker, marginTop: 14 }}>Impersonator precision · target ≥ 99.5% at the 95% lower bound</div>
          {run.precision.length === 0 ? (
            <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Nothing labelled likely impersonator in the window.</div>
          ) : (
            <TableScroll minWidth={700}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                <thead>
                  <tr>
                    {['Agent', 'Labelled', 'Contradicted', 'Fake cluster', 'Precision', '95% lower', 'Target'].map((h) => (
                      <th key={h} style={h === 'Agent' ? th : { ...th, textAlign: 'right' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {run.precision.map((p) => (
                    <tr key={p.agent_id}>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{p.agent_id}</td>
                      <td style={num}>{formatCount(p.labelled)}</td>
                      <td style={num}>{formatCount(p.contradicted)}</td>
                      <td style={num}>{formatCount(p.confirmed_fake_cluster)}</td>
                      <td style={num}>{formatRate(p.precision)}</td>
                      <td style={num}>{formatRate(p.precision_lower95)}</td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <Pill tone={p.meets_target ? 'lime' : 'neutral'}>{p.meets_target ? 'meets' : 'not yet'}</Pill>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
          {run.precision_with_labels !== undefined ? (
            <div data-testid="precision-with-labels">
              <div style={{ ...kicker, marginTop: 14 }}>
                With merchant labels · overturned disputes as weak labels{run.merchant_labels !== undefined
                  ? ` · ${formatCount(run.merchant_labels.examples)} examples from ${formatCount(run.merchant_labels.accounts)} accounts (weight ${run.merchant_labels.weight.toFixed(1)}, ≤ 5 per account)`
                  : ''}
              </div>
              {run.precision_with_labels.length === 0 ? (
                <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Nothing labelled likely impersonator in the window.</div>
              ) : (
                <TableScroll minWidth={560}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                    <thead>
                      <tr>
                        {['Agent', 'Labelled', 'Weak contradictions', 'Precision', '95% lower', 'Target'].map((h) => (
                          <th key={h} style={h === 'Agent' ? th : { ...th, textAlign: 'right' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {run.precision_with_labels.map((p) => (
                        <tr key={p.agent_id}>
                          <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{p.agent_id}</td>
                          <td style={num}>{formatCount(p.labelled)}</td>
                          <td style={num}>{p.weak_contradicted.toFixed(1)}</td>
                          <td style={num}>{formatRate(p.precision)}</td>
                          <td style={num}>{formatRate(p.precision_lower95)}</td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <Pill tone={p.meets_target ? 'lime' : 'neutral'}>{p.meets_target ? 'meets' : 'not yet'}</Pill>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </div>
          ) : null}
          {run.reason_contribution !== undefined ? <ReasonContributionTable rc={run.reason_contribution} /> : null}
        </>
      )}
    </Card>
  );

  const reportsTotal = data === null ? 0 : TIERS.reduce((a, t) => a + data.reports_7d[t], 0);

  return (
    <div style={{ padding: 24, maxWidth: 1040 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={kicker}>ADR-0231 / ADR-0232 / ADR-0236 · Agent Trust Engine</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Trust engine (shadow)</h1>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4 }}>
            Shadow statistics — nothing here is shown to merchants. Merchants see labels only on sites turned on in the panel below.
            {data !== null ? <> Rule version <span style={{ fontFamily: monoFont, color: C.body }}>{data.rule_version}</span>.</> : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* ADR-0235 — per-site flag + kill switch for merchant-visible verdicts. */}
      <div style={{ marginBottom: 16 }}>
        <LiveVerdictsPanel reloadToken={flagsToken} onChanged={() => setFlagsToken((n) => n + 1)} />
      </div>

      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>
            The trust engine API is not deployed yet. This page will work once the API release that adds it is live; nothing is broken in the meantime.
          </div>
        </Card>
      ) : null}

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div role="alert" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}

      {loading && data === null ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {data !== null ? (
        <>
          {section('Reports · last 7 days')}
          <Card style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {TIERS.map((t) => (
                <div key={t} data-testid={`reports-${t}`}>
                  {stat(TIER_LABEL[t], formatCount(data.reports_7d[t]), formatRate(share(data.reports_7d[t], reportsTotal)))}
                </div>
              ))}
              {stat('Active installs', formatCount(data.active_installs))}
              {stat('Rollup entities', formatCount(data.rollup_entities_7d), 'agent × network × day, 7 days')}
            </div>
          </Card>

          {section(`Shadow verdicts · last ${data.shadow.window_days} days`)}
          <Card style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              {stat('Total', formatCount(data.shadow.total))}
              {LABELS.map((l) => (
                <div key={l} data-testid={`label-${l}`}>
                  {stat(LABEL_LABEL[l], formatCount(data.shadow.by_label[l]), formatRate(share(data.shadow.by_label[l], data.shadow.total)))}
                </div>
              ))}
              {stat('Suspicious (flag)', formatCount(data.shadow.suspicious))}
              {data.labelled_examples !== undefined
                ? stat('Merchant labels', formatCount(data.labelled_examples.count), `overturned disputes · weight ${data.labelled_examples.weight.toFixed(1)}`)
                : null}
            </div>
            <div style={{ ...kicker, marginTop: 16 }}>By evidence tier</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TIERS.map((t) => (
                <Pill key={t} tone={t === 'anonymous' ? 'neutral' : 'sky'}>
                  {TIER_LABEL[t]}: {formatCount(data.shadow.by_tier[t])}
                </Pill>
              ))}
            </div>
            <div style={{ ...kicker, marginTop: 16 }}>Top reasons</div>
            {data.shadow.top_reasons.length === 0 ? (
              <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>No shadow verdicts yet.</div>
            ) : (
              <TableScroll minWidth={360}>
                <table style={{ borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 12.5 }}>
                  <tbody>
                    {data.shadow.top_reasons.map((r) => (
                      <tr key={r.reason}>
                        <td style={{ ...td, fontFamily: monoFont, fontSize: 11.5 }}>{r.reason}</td>
                        <td style={num}>{formatCount(r.count)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Card>

          {/* ADR-0236 — go-live readiness + the per-site "Turn on labels" gate. */}
          <ReadinessPanel reloadToken={flagsToken} onChanged={() => setFlagsToken((n) => n + 1)} />

          {data.baselines !== undefined ? <BaselinesPanel b={data.baselines} /> : null}

          {data.hardening !== undefined ? (
            <HardeningPanel
              h={data.hardening}
              onAck={ackAlert}
              includeAcked={includeAcked}
              onToggleAcked={setIncludeAcked}
            />
          ) : null}

          {section('Backtest · hide the proof')}
          {renderRun('Attested traffic', data.latest.attested, false)}
          {renderRun('Counterfactual run', data.latest.counterfactual, true)}
        </>
      ) : null}
    </div>
  );
}
